import { render } from 'lit-html';
import { cartFooterTemplate } from 'b2b-plus/ui-templates';
import { formatMoney } from 'b2b-plus/ui';
import { checkout, b2bPlusString } from 'b2b-plus/utils';
import { meetsMinimumOrderValuePerShipment } from 'b2b-plus/cart-sdk';

class B2BPlusCartFooter extends HTMLElement {

  /** @type {boolean} Guards against double-binding listeners. */
  #listenersAttached = false;

  /** @type {AbortController|null} For checkout listener cleanup on disconnect. */
  #checkoutListenersAbort = null;

  /** @type {Function|null} Callback invoked when checkout fails so the cart can unlock itself. */
  #onCheckoutError = null;

  /** @type {Function|null} Callback invoked when validation passes — opens the checkout popup. */
  #onCheckoutReady = null;

  /** @type {number} Distinct product tables on the cart (includes placeholders with zero units). */
  #cartProductTableCount = 0;

  /** @type {number} Last cart subtotal in cents (for CTA re-sync after failed checkout). */
  #lastSubtotal = 0;

  /** @type {Map<string, number>|Record<string, number>|null} Last per-shipment totals (cents). */
  #lastShipmentTotals = null;

  connectedCallback() {
    // Checkout button starts disabled. #syncCtaState() enables it once init() is
    // called with totalUnits > 0 or at least one product table (placeholders with no qty).
    render(cartFooterTemplate(), this);

    this.#listenersAttached = false;
    this.#checkoutListenersAbort?.abort();
    this.#checkoutListenersAbort = null;
  }

  disconnectedCallback() {
    this.#checkoutListenersAbort?.abort();
    this.#checkoutListenersAbort = null;
    this.#listenersAttached = false;
  }

  /**
   * Show "Resolve {count} issues to submit" beside the checkout button. `count` 0 hides it. The CTA itself stays enabled: clicking it
   * re-runs validation, which is how a buyer confirms their fixes.
   * @param {number} count
   */
  setIssueCount(count) {
    const el = this.querySelector('[data-footer-issues]');
    if (!el) return;
    const n = Number(count) || 0;
    el.hidden = n <= 0;
    const text = el.querySelector('[data-footer-issues-text]');
    if (text) {
      text.textContent = n > 0
        ? (b2bPlusString(n === 1 ? 'cartIssuesFooterOne' : 'cartIssuesFooterOther')
          || 'Resolve {count} issues to submit').replaceAll('{count}', String(n))
        : '';
    }
  }

  /**
   * Populate summary stats and sync the CTA enabled/disabled state.
   * Called by b2b-plus-cart after every totals update — initial load,
   * quick-add, and cart-saved events all go through here.
   *
   * @param {object} options
   * @param {number} options.totalUnits    - Sum of all line item quantities.
   * @param {number} options.totalProducts - Number of distinct products with positive units.
   * @param {number} options.subtotal      - Total price in cents.
   * @param {number} options.cartProductTableCount - Product tables in the cart (for CTA when units are 0).
   * @param {Map<string, number>|Record<string, number>|null} [options.shipmentTotals] - Per-shipment subtotals (cents).
   */
  init({
    totalUnits = 0,
    totalProducts = 0,
    subtotal = 0,
    cartProductTableCount = 0,
    shipmentTotals = null,
  } = {}) {
    const unitsEl    = this.querySelector('[data-footer-total-units]');
    const productsEl = this.querySelector('[data-footer-total-products]');
    const subtotalEl = this.querySelector('[data-footer-subtotal]');

    if (unitsEl)    unitsEl.textContent    = totalUnits;
    if (productsEl) productsEl.textContent = totalProducts;
    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);

    this.#cartProductTableCount = cartProductTableCount;
    this.#lastSubtotal = Number(subtotal) || 0;
    this.#lastShipmentTotals = shipmentTotals ?? null;
    this.#syncCtaState(totalUnits, cartProductTableCount, this.#lastShipmentTotals);

    // If a minimum-order-quantity error is showing and the cart now meets the
    // threshold, clear it. We intentionally only clear (never re-show) here so
    // the error stays anchored to a checkout attempt; a debounced totals
    // refresh that still falls short must not wipe the message.
    const minQty = window.b2bPlus.config?.cart?.minimunQuantity;
    const threshold = Number(minQty?.threshold) || 0;
    if (!minQty?.enable || threshold <= 0 || totalUnits >= threshold) {
      this.clearCheckoutError();
    }
  }

  /**
   * Show a checkout-blocking error message in the footer, directly below the
   * checkout button (e.g. minimum order quantity not met).
   * @param {string} message
   */
  showCheckoutError(message) {
    const el = this.querySelector('[data-footer-checkout-error]');
    if (!el) return;
    el.textContent = message ?? '';
    el.hidden = false;
  }

  /** Clear and hide the footer checkout-blocking error message. */
  clearCheckoutError() {
    const el = this.querySelector('[data-footer-checkout-error]');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  /**
   * Attach checkout click handlers. Must be called once after the cart data
   * is fully loaded so the `beforeCheckout` validation callback is available.
   *
   * Does NOT enable or disable the CTAs — that is handled entirely by
   * `#syncCtaState` via `init()`, which is also called on every quick-add and
   * cart-saved update so the button state always reflects the live cart.
   *
   * Idempotent: subsequent calls are no-ops.
   *
   * @param {object}   [options]
   * @param {Function} [options.beforeCheckout]  - Async callback; return false to abort.
   * @param {Function} [options.onCheckoutError] - Called when the checkout network call fails.
   * @param {Function} [options.onCheckoutReady] - Called when validation passes; opens the checkout popup.
   *                                               When provided, navigation to /checkout is suppressed.
   */
  enable({ beforeCheckout, onCheckoutError, onCheckoutReady } = {}) {
    this.#onCheckoutError = onCheckoutError ?? null;
    this.#onCheckoutReady = onCheckoutReady ?? null;
    if (this.#listenersAttached) return;
    this.#listenersAttached = true;
    this.#checkoutListenersAbort?.abort();
    const signal = (this.#checkoutListenersAbort = new AbortController()).signal;

    const checkoutBtn = this.querySelector('[data-footer-checkout]');

    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', async () => {
        if (beforeCheckout && !(await beforeCheckout())) return;
        
        if (this.#onCheckoutReady) {
          this.#onCheckoutReady();
        } else {
          this.#handleCheckout('/checkout');
        }
      }, { signal });
    }
  }

  /**
   * Checkout flow UI: label + optional inline spinner. Does not change enabled/disabled
   * (that stays with `#syncCtaState` and cart validation).
   *
   * @param {'idle'|'validating'|'proceeding'} state
   */
  setCheckoutProgress(state) {
    this.#setCheckoutProgress(state);
  }

  /** Re-syncs checkout CTA enabled/disabled from footer DOM (e.g. after a failed checkout). */
  syncCheckoutCtaFromTotals() {
    this.#syncCtaStateFromDom();
  }

  /**
   * Sync the IndexedDB draft into the Shopify cart and navigate to the native
   * Shopify checkout. Public wrapper over the internal fallback path so callers
   * (the cart element) can route normal B2B buyers / reps-shopping-as-self to
   * native checkout instead of the custom B2B popup.
   * @param {string} [path]
   * @returns {Promise<void>}
   */
  proceedToNativeCheckout(path = '/checkout') {
    return this.#handleCheckout(path);
  }

  // ── Private ──────────────────────────────────────────────────────────────

  /**
   * Read cart.minimumOrderValue config. Threshold is in cents.
   * @returns {{ enable: boolean, threshold: number }}
   */
  #minimumOrderValueConfig() {
    const cfg = window.b2bPlus?.config?.cart?.minimumOrderValue;
    return {
      enable: Boolean(cfg?.enable),
      threshold: Number(cfg?.threshold) || 0,
    };
  }

  /**
   * Whether every shipment total meets the configured minimum order value.
   * @param {Map<string, number>|Record<string, number>|null} [shipmentTotals]
   * @returns {boolean}
   */
  #meetsMinimumOrderValue(shipmentTotals = this.#lastShipmentTotals) {
    return meetsMinimumOrderValuePerShipment(shipmentTotals, this.#minimumOrderValueConfig());
  }

  /**
   * Show / hide the dedicated minimum-order-value alert and fill its amount.
   * @param {boolean} belowMinimum
   */
  #syncMinimumOrderAlert(belowMinimum) {
    const el = this.querySelector('[data-footer-minimum-order-error]');
    if (!el) return;

    if (!belowMinimum) {
      el.hidden = true;
      return;
    }

    const { threshold } = this.#minimumOrderValueConfig();
    const template = window.b2bPlus?.strings?.cartCheckoutMinOrderValue
      || 'Minimum order value of {amount}';
    el.textContent = template.replace('{amount}', formatMoney(threshold));
    el.hidden = false;
  }

  /**
   * Enable or disable the checkout CTA(s) based on whether there are line-item
   * units or at least one product table (e.g. quick-add placeholder), and whether
   * every shipment total meets the minimum order value. Called by `init()` on every
   * totals update.
   *
   * @param {number} totalUnits
   * @param {number} [cartProductTableCount]
   * @param {Map<string, number>|Record<string, number>|null} [shipmentTotals]
   */
  #syncCtaState(
    totalUnits,
    cartProductTableCount = this.#cartProductTableCount,
    shipmentTotals = this.#lastShipmentTotals,
  ) {
    const hasItems = totalUnits > 0 || cartProductTableCount > 0;
    const meetsMin = this.#meetsMinimumOrderValue(shipmentTotals);
    const belowMinimum = hasItems && !meetsMin;

    this.#syncMinimumOrderAlert(belowMinimum);

    const checkoutBtn = this.querySelector('[data-footer-checkout]');
    if (checkoutBtn) checkoutBtn.disabled = !hasItems || !meetsMin;
  }

  /** Re-applies CTA enabled/disabled from the live footer “total units” stat. */
  #syncCtaStateFromDom() {
    const raw = this.querySelector('[data-footer-total-units]')?.textContent?.trim() ?? '';
    const n     = raw === '' || raw === '—' ? 0 : Number(raw);
    this.#syncCtaState(Number.isFinite(n) ? n : 0, this.#cartProductTableCount, this.#lastShipmentTotals);
  }

  /**
   * @param {'idle'|'validating'|'proceeding'} state
   */
  #setCheckoutProgress(state) {
    const { strings: s } = window.b2bPlus;
    const cta = this.querySelector('[data-footer-checkout]');
    if (!cta) return;

    const label  = cta.querySelector('[data-footer-cta-label]');
    const loader = cta.querySelector('[data-footer-cta-loader]');

    if (state === 'idle') {
      cta.removeAttribute('aria-busy');
      if (loader) loader.setAttribute('hidden', '');
      if (label) label.textContent = s.cartCheckout;
      return;
    }

    cta.setAttribute('aria-busy', 'true');
    if (loader) loader.removeAttribute('hidden');

    if (!label) return;
    if (state === 'validating') {
      label.textContent = s.cartCheckoutInventoryChecking;
    } else if (state === 'proceeding') {
      label.textContent = s.cartCheckoutProceedingCart;
    }
  }

  /**
   * Fallback: syncs IndexedDB → Shopify cart then navigates to `path`.
   * Used only when no `onCheckoutReady` callback is provided.
   * @param {string} path
   */
  async #handleCheckout(path) {
    const { strings: s } = window.b2bPlus;

    const activeCta = this.querySelector('[data-footer-checkout]');
    if (activeCta) activeCta.disabled = true;

    this.#setCheckoutProgress('proceeding');

    try {
      await checkout({ path });
    } catch (err) {
      console.error('b2b-plus: checkout failed', err);
      const retry = s.cartCheckoutErrorRetry ?? '';
      alert(retry ? `${s.cartCheckoutError}\n\n${retry}` : s.cartCheckoutError);

      this.#onCheckoutError?.();
      this.#setCheckoutProgress('idle');
      this.syncCheckoutCtaFromTotals();
    }
  }

}

customElements.define('b2b-plus-cart-footer', B2BPlusCartFooter);
